import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit2, Trash2, Power, Upload, Image, Eye, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function BannersPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '', content: '', type: 'login_banner', banner_type: 'login_banner',
    priority: 1, bg_color: '#D4AF37', text_color: '#000000',
    image_url: '', link_url: '', status: 'active',
    start_at: '', expires_at: '',
  });

  useEffect(() => { fetchBanners(); }, []);

  // Tick every second so the "Scheduled · in 02:14:37" and "ends in …"
  // countdowns on each banner row update live without a manual refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await api.get('/api/admin/announcements');
      setBanners(res.data.announcements || res.data.items || []);
    } catch { toast.error('Failed to load banners'); }
    finally { setLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PNG, JPG, JPEG, WebP, GIF images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_type', 'banner');
      const res = await api.post('/api/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url || res.data.public_url;
      setFormData(p => ({ ...p, image_url: url }));
      setPreviewImage(URL.createObjectURL(file));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert datetime-local "YYYY-MM-DDTHH:MM" (local TZ) to ISO-8601 UTC
      // so the backend schedule filter `start_at <= now <= expires_at` can
      // compare against `datetime.now(timezone.utc).isoformat()`.
      const toIso = (local) => local ? new Date(local).toISOString() : null;
      const payload = {
        ...formData,
        banner_type: formData.type,
        is_master_only: true,
        start_at: toIso(formData.start_at),
        expires_at: toIso(formData.expires_at),
      };
      if (editing) {
        await api.put(`/api/admin/announcements/${editing.id}`, payload);
        toast.success('Banner updated');
      } else {
        await api.post('/api/admin/announcements', payload);
        toast.success('Banner created');
      }
      setShowForm(false); setEditing(null); resetForm(); fetchBanners();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await api.delete(`/api/admin/announcements/${id}`);
      toast.success('Deleted'); fetchBanners();
    } catch { toast.error('Failed'); }
  };

  const handleToggle = async (banner) => {
    try {
      await api.put(`/api/admin/announcements/${banner.id}`, { ...banner, status: banner.status === 'active' ? 'inactive' : 'active' });
      toast.success('Status updated'); fetchBanners();
    } catch { toast.error('Failed'); }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', type: 'login_banner', banner_type: 'login_banner', priority: 1, bg_color: '#D4AF37', text_color: '#000000', image_url: '', link_url: '', status: 'active', start_at: '', expires_at: '' });
    setPreviewImage(null);
  };

  const openEdit = (b) => {
    setEditing(b);
    // datetime-local inputs need 'YYYY-MM-DDTHH:MM' not full ISO.
    const toLocal = (iso) => {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ''; }
    };
    setFormData({ title: b.title || '', content: b.content || '', type: b.type || b.banner_type || 'login_banner', banner_type: b.banner_type || b.type || 'login_banner', priority: b.priority || 1, bg_color: b.bg_color || '#D4AF37', text_color: b.text_color || '#000000', image_url: b.image_url || '', link_url: b.link_url || '', status: b.status || 'active', start_at: toLocal(b.start_at), expires_at: toLocal(b.expires_at) });
    setPreviewImage(b.image_url ? resolveUrl(b.image_url) : null);
    setShowForm(true);
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  const filtered = filterType === 'all' ? banners : banners.filter(b => (b.type || b.banner_type) === filterType);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="banners-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Announcements & Banners</h2>
          <p className="text-sm text-gray-400">Upload poster images that display across all screens.</p>
        </div>
        <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-banner-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Banner
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-1 bg-[#131822] rounded-lg p-1 max-w-md">
        {[{ id: 'all', label: 'All' }, { id: 'login_banner', label: 'Landing Page' }, { id: 'home_banner', label: 'Player Home' }, { id: 'announcement_popup', label: 'Announcement Popup' }].map(f => (
          <button key={f.id} onClick={() => setFilterType(f.id)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium ${filterType === f.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>
            {f.label} ({f.id === 'all' ? banners.length : banners.filter(b => (b.type || b.banner_type) === f.id).length})
          </button>
        ))}
      </div>

      {/* Banner List */}
      <div className="grid gap-3">
        {filtered.map(b => {
          // Compute schedule state: live | scheduled | expired | always-on.
          const now = new Date();
          const startAt = b.start_at ? new Date(b.start_at) : null;
          const expiresAt = b.expires_at ? new Date(b.expires_at) : null;
          let sched = 'always';
          if (startAt && startAt > now) sched = 'scheduled';
          else if (expiresAt && expiresAt < now) sched = 'expired';
          else if (b.status === 'active') sched = 'live';
          else sched = 'disabled';

          // Human-readable countdown.
          const msLeft = sched === 'scheduled' && startAt ? startAt - now
                        : sched === 'live' && expiresAt ? expiresAt - now : null;
          const fmtLeft = (ms) => {
            if (ms == null || ms < 0) return '';
            const total = Math.floor(ms / 1000);
            const d = Math.floor(total / 86400);
            const h = Math.floor((total % 86400) / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = total % 60;
            if (d >= 1) return `${d}d ${h}h`;
            if (h >= 1) return `${h}h ${m}m`;
            if (m >= 1) return `${m}m ${s}s`;
            return `${s}s`;
          };

          const pill = {
            live:      { bg: 'bg-green-500/20',  fg: 'text-green-400',  text: '✓ Live' },
            scheduled: { bg: 'bg-amber-500/20',  fg: 'text-amber-400',  text: `⏰ Scheduled · in ${fmtLeft(msLeft)}` },
            expired:   { bg: 'bg-gray-500/20',   fg: 'text-gray-400',   text: '⌛ Expired' },
            disabled:  { bg: 'bg-red-500/20',    fg: 'text-red-400',    text: '✗ Disabled' },
            always:    { bg: 'bg-blue-500/20',   fg: 'text-blue-400',   text: '∞ Always-on' },
          }[sched];

          return (
          <div key={b.id} className={`glass-card rounded-xl overflow-hidden ${sched === 'expired' || sched === 'disabled' ? 'opacity-50' : ''}`} data-testid={`banner-${b.id}`}>
            <div className="flex items-stretch">
              {/* Preview - Image or Color */}
              {b.image_url ? (
                <div className="w-48 h-32 flex-shrink-0 bg-black/30 flex items-center justify-center overflow-hidden">
                  <img src={resolveUrl(b.image_url)} alt={b.title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-48 p-4 flex items-center justify-center flex-shrink-0" style={{ background: b.bg_color || '#D4AF37', color: b.text_color || '#000' }}>
                  <div className="text-center">
                    <p className="font-bold text-sm leading-tight">{b.title}</p>
                    <p className="text-xs mt-1 opacity-80 line-clamp-2">{b.content}</p>
                  </div>
                </div>
              )}
              {/* Details */}
              <div className="flex-1 p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <h3 className="font-medium text-sm">{b.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${(b.type || b.banner_type) === 'login_banner' ? 'bg-purple-500/20 text-purple-400' : (b.type || b.banner_type) === 'home_banner' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-300'}`}>
                      {(b.type || b.banner_type) === 'login_banner' ? 'LANDING' : (b.type || b.banner_type) === 'home_banner' ? 'HOME' : 'POPUP'}
                    </span>
                    <span className="text-[10px] text-gray-500">P:{b.priority}</span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${pill.bg} ${pill.fg}`}
                      data-testid={`banner-schedule-pill-${b.id}`}
                      data-state={sched}
                    >
                      {pill.text}
                    </span>
                    {sched === 'live' && expiresAt && msLeft != null && (
                      <span className="text-[10px] text-gray-500" data-testid={`banner-expires-in-${b.id}`}>
                        ends in {fmtLeft(msLeft)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-1">{b.content}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleToggle(b)} className="text-xs h-7"><Power className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} className="text-xs h-7"><Edit2 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(b.id)} className="text-xs h-7 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          </div>
        );
        })}
        {filtered.length === 0 && <p className="text-center text-gray-500 py-8">No banners found</p>}
      </div>

      {/* Form Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Banner' : 'New Banner'} maxWidth="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Banner Location</label>
            <div className="flex gap-2">
                {[{ v: 'login_banner', l: 'Landing Page Banner' }, { v: 'home_banner', l: 'Player Home Banner' }, { v: 'announcement_popup', l: 'Announcement Popup' }].map(o => (
                <button key={o.v} type="button" onClick={() => setFormData(p => ({ ...p, type: o.v, banner_type: o.v }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium ${formData.type === o.v ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <Input value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="Banner Title" className="bg-[#0B0E14] border-white/10" required data-testid="banner-title" />
          <textarea value={formData.content} onChange={e => setFormData(p => ({ ...p, content: e.target.value }))} placeholder="Banner Content/Message" rows={2} className="w-full rounded-md bg-[#0B0E14] border border-white/10 text-white p-3 text-sm" />

          {/* Image Upload */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Poster Image (PNG, JPG, JPEG, WebP)</label>
            <div
              className="text-[11px] mb-2 rounded-md px-2.5 py-1.5 space-y-0.5"
              style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)', color: '#F5D87A' }}
              data-testid="banner-size-hint"
            >
              {formData.type === 'login_banner' ? (
                <>
                  <div>📐 <b>Width: 800 px · Height: 300 px</b> (ratio 2.67:1)</div>
                  <div className="opacity-80">Mobile crops to ~400 × 144 px · Max file size: 5 MB</div>
                </>
              ) : (
                <>
                  <div>📐 <b>Width: 1200 px · Height: 400 px</b> (ratio 3:1)</div>
                  <div className="opacity-80">Mobile crops to ~400 × 144 px · Desktop shows ~1200 × 144 px · Max 5 MB</div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" onChange={handleImageUpload} className="hidden" />
              <Button type="button" onClick={() => fileInputRef.current?.click()} variant="outline"
                className="border-white/10 flex-1" disabled={uploading} data-testid="banner-upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : formData.image_url ? 'Change Image' : 'Upload Image'}
              </Button>
              {formData.image_url && (
                <button type="button" onClick={() => { setFormData(p => ({ ...p, image_url: '' })); setPreviewImage(null); }}
                  className="p-2 rounded-lg hover:bg-white/5 text-red-400"><X className="w-4 h-4" /></button>
              )}
            </div>
            {/* Preview */}
            {(previewImage || formData.image_url) && (
              <div className="mt-2 rounded-lg overflow-hidden border border-white/10 bg-black/30">
                <img src={previewImage || resolveUrl(formData.image_url)} alt="Preview"
                  className="w-full h-auto max-h-64 object-contain" data-testid="banner-preview-img" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Background Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={formData.bg_color} onChange={e => setFormData(p => ({ ...p, bg_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={formData.bg_color} onChange={e => setFormData(p => ({ ...p, bg_color: e.target.value }))} className="bg-[#0B0E14] border-white/10 flex-1" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Text Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={formData.text_color} onChange={e => setFormData(p => ({ ...p, text_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={formData.text_color} onChange={e => setFormData(p => ({ ...p, text_color: e.target.value }))} className="bg-[#0B0E14] border-white/10 flex-1" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Priority (1=highest)</label>
              <Input type="number" min={1} max={10} value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: parseInt(e.target.value) || 1 }))} className="bg-[#0B0E14] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Link URL (optional)</label>
              <Input value={formData.link_url} onChange={e => setFormData(p => ({ ...p, link_url: e.target.value }))} placeholder="https://..." className="bg-[#0B0E14] border-white/10" />
            </div>
          </div>

          {/* Scheduling window — leave blank for immediate / open-ended. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-400 block">Schedule (optional)</label>
              <span className="text-[10px] text-gray-500">Leave empty for always-on</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Start at</label>
                <Input
                  type="datetime-local"
                  value={formData.start_at}
                  onChange={e => setFormData(p => ({ ...p, start_at: e.target.value }))}
                  className="bg-[#0B0E14] border-white/10"
                  data-testid="banner-start-at"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Expires at</label>
                <Input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={e => setFormData(p => ({ ...p, expires_at: e.target.value }))}
                  className="bg-[#0B0E14] border-white/10"
                  data-testid="banner-expires-at"
                />
              </div>
            </div>
          </div>

          {/* Live Preview */}
          {!formData.image_url && (
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Color Preview</label>
              <div className="rounded-lg p-4" style={{ background: formData.bg_color, color: formData.text_color }}>
                <p className="font-bold text-sm">{formData.title || 'Banner Title'}</p>
                <p className="text-xs mt-1 opacity-80">{formData.content || 'Banner content goes here...'}</p>
              </div>
            </div>
          )}

          <Button type="submit" className="w-full bg-[#D4AF37] text-black" data-testid="banner-submit">{editing ? 'Update' : 'Create'} Banner</Button>
        </form>
      </Modal>
    </div>
  );
}
